import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import ConfirmationModal from '../../utils/ConfirmationModal';
import ThemedSelect from '../../utils/ThemedSelect';
import TrimContent from '../../utils/TrimContent';
import authNavigate from '../../globalComponents/AuthNav';

const API_BASE_URL = '/pceapi/threads/unstructured';

// Same fields, in the same order, as the Metadata Editor table.
const METADATA_FIELDS = [
  { key: 'title', label: 'Title', rows: 2 },
  { key: 'description', label: 'Description', rows: 4 },
  { key: 'publisher', label: 'Publisher', rows: 1 },
  { key: 'authors', label: 'Authors', rows: 2, isArray: true, hint: 'Separate with commas' },
  { key: 'organization', label: 'Organization', rows: 1 },
  { key: 'keywords', label: 'Keywords', rows: 2, isArray: true, hint: 'Separate with commas' },
  { key: 'year', label: 'Year', rows: 1 },
  { key: 'journal_page_no', label: 'Journal Page No', isJournal: true },
  { key: 'classification', label: 'Document Tag', isTag: true },
  { key: 'doi', label: 'DOI', rows: 1 },
  { key: 'website_url', label: 'Website URL', isUrl: true },
  { key: 'accessed_date', label: 'Accessed Date', isDate: true },
];

// Journal Page No is ONE text column (journal_page_no). It is edited as
// Issue / Pages / Volume and saved back into that same column in the format
// the existing data already uses:
//   "vol [vol], no [issue], pp [start]-[end]"      e.g. "vol 335, no 7, pp 462-7"
// Empty parts are left out. A leading journal name ("Stroke, vol 42, ...") is
// kept as it is. Reads "pp"/"pp.", "no"/"num"/"issue", with or without commas.
const splitJournal = (text) =>
  (text.includes(',') ? text : text.replace(/\s+(?=(?:vol|no|num|issue|pp)\b)/gi, ', '))
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

const parseJournal = (value) => {
  const text = (value || '').trim();
  const result = { prefix: '', vol: '', start: '', end: '', issue: '', legacy: false };
  if (!text) return result;

  const prefix = [];
  let matched = false;
  for (const part of splitJournal(text)) {
    let m;
    if ((m = /^vol(?:ume)?\.?\s*(.+)$/i.exec(part))) {
      result.vol = m[1].trim(); matched = true;
    } else if ((m = /^(?:no|num|issue)\.?\s*(.+)$/i.exec(part))) {
      result.issue = m[1].trim(); matched = true;
    } else if ((m = /^pp?\.?\s*([^\s\-–]+)(?:\s*[-–]\s*(\S+))?$/i.exec(part))) {
      result.start = m[1]; result.end = m[2] || ''; matched = true;
    } else if (!matched) {
      prefix.push(part);          // journal name before the first vol/no/pp
    } else {
      return { ...result, legacy: true };   // unknown text after the numbers
    }
  }
  if (!matched) return { ...result, legacy: true };
  result.prefix = prefix.join(', ');
  return result;
};

const formatJournal = ({ prefix, vol, start, end, issue }) => {
  const parts = [];
  if (prefix) parts.push(prefix);
  if (vol) parts.push(`vol ${vol}`);
  if (issue) parts.push(`no ${issue}`);
  const pages = start && end ? `${start}-${end}` : (start || end);
  if (pages) parts.push(`pp ${pages}`);
  return parts.join(', ');
};

// Spaces, commas and dashes would break the stored format, so they are not typed in.
const cleanJournalPart = (v) => v.replace(/[\s,\-–]+/g, '');

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isHttpUrl = (value) => /^https?:\/\/\S+$/i.test((value || '').trim());

const TEXT_EXTENSIONS = ['txt'];
// Shown through Microsoft's Office web viewer, which fetches the file itself
// from the short-lived link. (.docx normally arrives here as its PDF copy.)
const OFFICE_EXTENSIONS = ['ppt', 'pptx', 'xls', 'xlsx', 'doc', 'docx'];
const OFFICE_VIEWER_URL = 'https://view.officeapps.live.com/op/embed.aspx?src=';
// Big CSVs are cut to this many rows so the table stays responsive.
const CSV_MAX_ROWS = 500;

// Small RFC 4180 CSV parser: commas, quoted fields, "" escapes, and line
// breaks inside quotes. Returns an array of rows (arrays of strings).
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      rows.push(row); row = [];
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  // Drop fully empty trailing lines.
  while (rows.length && rows[rows.length - 1].every(c => c === '')) rows.pop();
  return rows;
};

const toDisplay = (value) => (Array.isArray(value) ? value.join(', ') : (value ?? ''));
const toArray = (value) =>
  typeof value === 'string'
    ? value.split(',').map(v => v.trim()).filter(Boolean)
    : (value || []);
const fileExtension = (name) => {
  const match = /\.([a-z0-9]+)$/i.exec(name || '');
  return match ? match[1].toLowerCase() : '';
};

// ─── Left side: the document itself ───────────────────────────────────────
// Asks the backend for a short-lived link (Word files come back as their PDF
// copy), downloads it and shows PDFs inline and TXT/CSV as text. Anything the
// browser cannot show inline gets an "Open in New Tab" button instead.
const DocumentViewer = ({ docId, documentName, metadata }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: false, error: '', kind: null, src: null, text: '', rows: null, rawUrl: null });
  const [retry, setRetry] = useState(0);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!docId) return undefined;
    const controller = new AbortController();
    const releaseBlob = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    releaseBlob();
    setState({ loading: true, error: '', kind: null, src: null, text: '', rows: null, rawUrl: null });

    const load = async () => {
      try {
        // POST /document-preview with { doc_id } (unstructured_thread_controller).
        const response = await axios.post(
          `${API_BASE_URL}/document-preview`,
          { doc_id: docId },
          { headers: { Authorization: GetCookie('access_token') }, signal: controller.signal }
        );
        const data = response.data;
        if (data.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
          return;
        }
        if (data.status !== 'success' || !data.preview_url) {
          setState(s => ({ ...s, loading: false, error: data.message || 'Preview not available' }));
          return;
        }

        const ext = data.extension || fileExtension(data.filename || documentName);
        const base = { loading: false, error: '', src: null, text: '', rows: null, rawUrl: data.preview_url };

        if (OFFICE_EXTENSIONS.includes(ext)) {
          setState({ ...base, kind: 'office', src: OFFICE_VIEWER_URL + encodeURIComponent(data.preview_url) });
          return;
        }
        if (!['pdf', 'csv', ...TEXT_EXTENSIONS].includes(ext)) {
          setState({ ...base, kind: 'external' });
          return;
        }

        const fileResponse = await fetch(data.preview_url, { signal: controller.signal });
        if (!fileResponse.ok) throw new Error(`Failed to fetch document: ${fileResponse.status}`);

        if (ext === 'csv') {
          const rows = parseCsv(await fileResponse.text());
          setState({ ...base, kind: 'csv', rows });
        } else if (ext === 'pdf') {
          const blob = await fileResponse.blob();
          const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          blobUrlRef.current = url;
          setState({ ...base, kind: 'pdf', src: url });
        } else if (TEXT_EXTENSIONS.includes(ext)) {
          const text = await fileResponse.text();
          setState({ ...base, kind: 'text', text });
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        console.error('Failed to load document preview:', error);
        setState(s => ({ ...s, loading: false, error: 'Failed to load document' }));
      }
    };

    load();
    return () => {
      controller.abort();
      releaseBlob();
    };
  }, [docId, retry]);

  const authors = toDisplay(metadata?.authors);

  return (
    <div className="ev-card-2 p-3 h-100 d-flex flex-column">
      {/* Header: title, authors, year — the same summary the review panel shows */}
      <div className="mb-3 flex-shrink-0">
        <p className="ev-bold-reg ev-med-p-blue mb-1 text-break">
          {metadata?.title || documentName || 'Untitled Document'}
        </p>
        {metadata?.title && documentName && (
          <p className="ev-xsmall-p-dark mb-0 text-break">
            <i className="bi bi-file-earmark me-1"></i>{documentName}
          </p>
        )}
        {authors && <p className="ev-xsmall-p-dark mb-0">Authors: {authors}</p>}
        {metadata?.year && <p className="ev-xsmall-p-dark mb-0">Year: {metadata.year}</p>}
      </div>

      <div
        className="flex-grow-1 position-relative ev-min-w-0"
        style={{ minHeight: state.kind === 'csv' || state.kind === 'text' ? 0 : undefined }}
      >
        {state.loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading document...</span>
            </div>
          </div>
        ) : state.error ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100">
            <i className="bi bi-exclamation-triangle ev-fs-48 text-warning mb-2"></i>
            <p className="ev-small-p-dark mb-2">{state.error}</p>
            <div className="d-flex gap-2">
              <button className="ev-btn-blue-fill-round ev-btn-small" onClick={() => setRetry(r => r + 1)}>
                Retry
              </button>
              {state.rawUrl && (
                <button className="ev-btn-blue-outline-round ev-btn-small" onClick={() => window.open(state.rawUrl, '_blank')}>
                  Open in New Tab
                </button>
              )}
            </div>
          </div>
        ) : state.kind === 'pdf' ? (
          <iframe
            src={state.src}
            className="w-100 h-100 ev-radius-small border"
            title={documentName || 'Document preview'}
          ></iframe>
        ) : state.kind === 'office' ? (
          <div className="h-100 d-flex flex-column">
            <iframe
              src={state.src}
              className="w-100 flex-grow-1 ev-radius-small border"
              title={documentName || 'Document preview'}
            ></iframe>
            <div className="d-flex justify-content-end align-items-center gap-2 mt-2 flex-shrink-0">
              <span className="ev-xsmall-p-dark">Not loading?</span>
              <button className="ev-btn-blue-outline-round ev-btn-xsmall" onClick={() => window.open(state.rawUrl, '_blank')}>
                Open in New Tab
              </button>
            </div>
          </div>
        ) : state.kind === 'csv' ? (
          state.rows && state.rows.length > 0 ? (
            <div className="h-100 d-flex flex-column">
              <div className="scrollable-element h-auto flex-grow-1 overflow-auto ev-card-3 ev-radius-small p-2">
                <table className="table-custom1 mt-0 w-100">
                  <thead>
                    <tr>
                      {state.rows[0].map((cell, i) => (
                        <th key={i} className="text-nowrap"><label>{cell}</label></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {state.rows.slice(1, CSV_MAX_ROWS + 1).map((row, r) => (
                      <tr key={r}>
                        {state.rows[0].map((_, c) => (
                          <td key={c} className="align-top ev-small-p-dark">{row[c] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="ev-xsmall-p-dark mb-0 mt-2 flex-shrink-0">
                {state.rows.length - 1 > CSV_MAX_ROWS
                  ? `Showing first ${CSV_MAX_ROWS} of ${state.rows.length - 1} rows.`
                  : `${state.rows.length - 1} rows.`}
              </p>
            </div>
          ) : (
            <p className="ev-small-p-dark text-center my-4">This CSV file is empty.</p>
          )
        ) : state.kind === 'text' ? (
          <pre className="ev-card-3 h-100 m-0 p-3 scrollable-element ev-small-p-dark text-wrap">
            {state.text}
          </pre>
        ) : state.kind === 'external' ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center">
            <i className="bi bi-file-earmark ev-fs-48 text-muted mb-2"></i>
            <p className="ev-small-p-dark mb-2">This file type can't be shown here.</p>
            <button className="ev-btn-blue-fill-round ev-btn-small" onClick={() => window.open(state.rawUrl, '_blank')}>
              Open in New Tab
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────
/**
 * DocumentMetadataPanel
 * Document panel opened from a document's "View & Edit" button, shown in
 * place of the Data Folder details.
 *  - Left: the document preview.
 *  - Right: the document's metadata. Editable in place when the user may edit
 *    metadata (canEditMetadata), read-only otherwise.
 *  - Previous / Next walk the documents of the current page (navItems).
 * Edits are kept per document while moving between them and saved together
 * with Update, through the same update-metadata call the Metadata Editor uses.
 */
const DocumentMetadataPanel = ({
  isOpen,
  onClose,
  docId,
  navItems = [],          // [{ id, name }] in the order shown in the list
  metadataList = [],      // rows from /document-metadata (same page)
  threadId,
  threadTags = [],
  canEditMetadata = false,
  onSaved,
}) => {
  const navigate = useNavigate();
  const [currentId, setCurrentId] = useState(docId);
  const [edits, setEdits] = useState({});        // { [docId]: { field: value } }
  // Fields are read-only until the user clicks Edit; edits then stay in
  // `edits` while moving between documents, until Update or Cancel.
  const [isEditing, setIsEditing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCurrentId(docId);
      setEdits({});
      setIsEditing(false);
    }
  }, [isOpen, docId]);

  const index = navItems.findIndex(item => String(item.id) === String(currentId));
  const current = index >= 0 ? navItems[index] : { id: currentId, name: '' };
  const metadataRow = useMemo(
    () => metadataList.find(m => String(m.doc_id) === String(currentId)),
    [metadataList, currentId]
  );
  const metadata = metadataRow?.metadata || {};
  const documentName = metadataRow?.document_name || current.name;

  const currentEdits = edits[currentId] || {};
  const editedDocCount = Object.keys(edits).filter(id => Object.keys(edits[id]).length > 0).length;

  const valueOf = (field) =>
    currentEdits[field] !== undefined ? currentEdits[field] : toDisplay(metadata[field]);

  const setField = (field, value) => {
    setEdits(prev => {
      const docEdits = { ...(prev[currentId] || {}) };
      if (value === toDisplay(metadata[field])) delete docEdits[field];
      else docEdits[field] = value;
      return { ...prev, [currentId]: docEdits };
    });
  };

  const goTo = (offset) => {
    const next = navItems[index + offset];
    if (next) setCurrentId(next.id);
  };

  const cancelEditing = () => {
    setEdits({});
    setIsEditing(false);
  };

  const requestClose = () => {
    if (editedDocCount > 0) setConfirmClose(true);
    else onClose();
  };

  const handleUpdate = async () => {
    const payload = {};
    Object.entries(edits).forEach(([id, fields]) => {
      if (Object.keys(fields).length === 0) return;
      payload[id] = {};
      Object.entries(fields).forEach(([field, value]) => {
        const def = METADATA_FIELDS.find(f => f.key === field);
        payload[id][field] = def?.isArray ? toArray(value) : value;
      });
    });
    if (Object.keys(payload).length === 0) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/update-metadata`,
        { thread_id: threadId, metadata: payload },
        { headers: { Authorization: GetCookie('access_token') } }
      );
      const data = response.data;
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      if (data.status === 'failed') {
        setErrorMessage(data.message || 'Failed to save metadata');
        return;
      }
      setEdits({});
      setIsEditing(false);
      setSuccessMessage(data.message || 'Metadata saved successfully');
      if (onSaved) onSaved();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save metadata');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tagOptions = [...new Set([...threadTags, 'unclassified'])].map(t => ({ value: t, label: t }));

  const renderField = (field) => {
    const value = valueOf(field.key);
    const changed = currentEdits[field.key] !== undefined;

    if (!canEditMetadata || !isEditing) {
      if (field.isUrl && isHttpUrl(value)) {
        return (
          <a
            href={value.trim()}
            target="_blank"
            rel="noopener noreferrer"
            className="ev-small-p-blue ev-link-accent text-break"
          >
            {value} <i className="bi bi-box-arrow-up-right ms-1"></i>
          </a>
        );
      }
      return (
        <p className="ev-small-p-dark mb-0 text-break">
          {value || <span className="text-muted">Not set</span>}
        </p>
      );
    }

    if (field.isJournal) {
      const parts = parseJournal(value);
      const setPart = (key, raw) => {
        const base = parts.legacy ? { prefix: '', vol: '', start: '', end: '', issue: '' } : parts;
        setField(field.key, formatJournal({ ...base, [key]: cleanJournalPart(raw) }));
      };
      const box = `formField4 ev-fs-12 w-100 ${changed ? 'border border-warning' : ''}`;
      return (
        <>
          {parts.legacy && (
            <p className="ev-xsmall-p-dark mb-1">
              Current value: <b>{value}</b>. It is replaced once you fill in the fields below.
            </p>
          )}
          <div className="d-flex align-items-end gap-2">
            <div className="flex-grow-1">
              <small className="ev-xsmall-p-dark">Issue</small>
              <input type="text" className={box} value={parts.issue}
                onChange={(e) => setPart('issue', e.target.value)} aria-label="Issue" />
            </div>
            <div className="flex-grow-1">
              <small className="ev-xsmall-p-dark">Pages</small>
              <div className="d-flex align-items-center gap-1">
                <input type="text" className={box} placeholder="Start" value={parts.start}
                  onChange={(e) => setPart('start', e.target.value)} aria-label="Page start" />
                <span className="ev-small-p-dark">–</span>
                <input type="text" className={box} placeholder="End" value={parts.end}
                  onChange={(e) => setPart('end', e.target.value)} aria-label="Page end" />
              </div>
            </div>
            <div className="flex-grow-1">
              <small className="ev-xsmall-p-dark">Volume</small>
              <input type="text" className={box} value={parts.vol}
                onChange={(e) => setPart('vol', e.target.value)} aria-label="Volume" />
            </div>
          </div>
          <small className="ev-xsmall-p-dark opacity-75">
            Saved as: {formatJournal(parts.legacy ? {} : parts) || '—'}
          </small>
        </>
      );
    }

    if (field.isUrl) {
      return (
        <input
          type="url"
          className={`formField4 ev-fs-12 w-100 ${changed ? 'border border-warning' : ''}`}
          placeholder="https://"
          value={value}
          onChange={(e) => setField(field.key, e.target.value)}
        />
      );
    }

    // Date picker for empty or YYYY-MM-DD values. A date already stored in
    // another format stays a text box, so it is never silently blanked.
    if (field.isDate) {
      const asDate = !value || ISO_DATE.test(value);
      return (
        <input
          type={asDate ? 'date' : 'text'}
          className={`formField4 ev-fs-12 w-100 ${changed ? 'border border-warning' : ''}`}
          value={value}
          onChange={(e) => setField(field.key, e.target.value)}
        />
      );
    }

    if (field.isTag) {
      return (
        <ThemedSelect
          value={value}
          options={tagOptions}
          onChange={(tag) => setField(field.key, tag)}
          placeholder="Select tag"
          ariaLabel="Document tag"
        />
      );
    }

    return (
      <>
        <textarea
          className={`formField4 ev-fs-12 w-100 ${changed ? 'border border-warning' : ''}`}
          rows={field.rows}
          value={value}
          onChange={(e) => setField(field.key, e.target.value)}
        />
        {field.hint && <small className="ev-xsmall-p-dark opacity-75">{field.hint}</small>}
      </>
    );
  };

  // Rendered inside the page's content area (not a full-screen overlay), so
  // the sidebar, top navigation and footer stay visible around it.
  return (
    <>
      <div>
        <div className="ev-card-3 p-3 vh-75 d-flex flex-column">

          {/* Header: title · Previous / ID / Next · Update / Close Panel */}
          <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-shrink-0">
            <p className="ev-med-p-blue mb-0 text-nowrap">Document Details</p>

            <div className="ev-card-2 p-1 d-flex align-items-center gap-2 flex-grow-1">
              <button
                type="button"
                className="ev-btn-light-fill px-3"
                disabled={index <= 0}
                onClick={() => goTo(-1)}
              >
                <i className="bi bi-chevron-left me-1"></i>Previous
              </button>
              {/* Document name, trimmed; full name on hover */}
              <span
                className="ev-btn-light-fill px-3 ev-min-w-0 text-nowrap avatar-tooltip"
                data-tooltip={documentName || 'Unnamed Document'}
              >
                <i className="bi bi-file-earmark-text me-1"></i>
                <TrimContent text={documentName || 'Unnamed Document'} maxLength={45} />
              </span>
              <button
                type="button"
                className="ev-btn-light-fill px-3"
                disabled={index < 0 || index >= navItems.length - 1}
                onClick={() => goTo(1)}
              >
                Next<i className="bi bi-chevron-right ms-1"></i>
              </button>
              {navItems.length > 0 && index >= 0 && (
                <span className="ev-small-p-dark ms-1">{index + 1} of {navItems.length} on this page</span>
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              {canEditMetadata && isEditing && (
                <button
                  type="button"
                  className="ev-btn-blue-fill-round ev-bold-reg"
                  disabled={editedDocCount === 0 || saving}
                  onClick={handleUpdate}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Update{editedDocCount > 1 ? ` (${editedDocCount} documents)` : ''}
                </button>
              )}
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg" onClick={requestClose}>
                Close Panel
              </button>
            </div>
          </div>

          {/* Body takes all the height left under the header */}
          <div className="row g-0 flex-grow-1 overflow-hidden">
            {/* LEFT: document */}
            <div className="col-md-8 pe-2 h-100">
              <DocumentViewer docId={currentId} documentName={documentName} metadata={metadata} />
            </div>

            {/* RIGHT: metadata */}
            <div className="ev-card-2 col-md-4 h-100 scrollable-element p-2 ev-radius-small">
              <div className="ev-card-3 p-2 mb-2 ev-radius-xsmall d-flex justify-content-between align-items-center">
                <p className="ev-bold-med ev-reg-p-dark mb-0">
                  {isEditing ? 'Edit Metadata' : 'Metadata'}
                </p>
                {canEditMetadata && metadataRow && (
                  isEditing ? (
                    <button
                      type="button"
                      className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
                      onClick={cancelEditing}
                    >
                      <i className="bi bi-x-lg me-1"></i>Cancel
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg"
                      onClick={() => setIsEditing(true)}
                    >
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                  )
                )}
              </div>

              {!metadataRow ? (
                <p className="ev-small-p-dark text-center my-4">
                  No metadata found for this document.
                </p>
              ) : (
                <div className="ev-card-3 p-2 ev-radius-xsmall">
                  {METADATA_FIELDS.map(field => (
                    <div key={field.key} className="mb-3">
                      <p className="ev-small-p-blue ev-bold-med mb-1">
                        {field.label}
                        {currentEdits[field.key] !== undefined && (
                          <span className="badge badge-xsm bg-warning ms-2">Edited</span>
                        )}
                      </p>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {saving && <ProcessingPopup message="Saving metadata" />}
      {errorMessage && <ErrorPopup message={errorMessage} onClose={() => setErrorMessage('')} />}
      {successMessage && <SuccessPopup message={successMessage} onClose={() => setSuccessMessage('')} />}

      <ConfirmationModal
        show={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={() => { setConfirmClose(false); onClose(); }}
        title="Unsaved metadata changes"
        message={`You have unsaved changes on ${editedDocCount} document${editedDocCount > 1 ? 's' : ''}. They are only saved when you click Update. If you close now, they will be lost.`}
        confirmButtonText="Close without saving"
        confirmButtonClass="btn-primary"
        cancelButtonText="Keep editing"
      />
    </>
  );
};

export default DocumentMetadataPanel;